<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcProjectGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    project: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.project,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('project');
    }
  );

  const act_2095089031469157_emit_7f6wg42mi_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_rx76l8bcx_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_mg831yxx7_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ProjectEditor',
      props: {
        target: 'general',
        project: _.cloneDeep(props.project),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_przvt8ull =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_n7oxalmzg =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_przvt8ull,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_n7oxalmzg],
    event: 'reload',
  });
  const act_2095089031469157_emit_7f6wg42mi = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_7f6wg42mi_getActionArgs,
  });
  const inst_evh_2095089031469157_close_rx76l8bcx =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_7f6wg42mi,
      event: 'close',
      when: inst_evh_2095089031469157_close_rx76l8bcx_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_mg831yxx7 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_mg831yxx7_getActionArgs,
      events: [inst_evh_2095089031469157_close_rx76l8bcx],
    });
  const inst_evh_2095089031469157_clickPreventStop_38pvm5k9j =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_mg831yxx7,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_38pvm5k9j],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_n7oxalmzg,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_rx76l8bcx,
    inst_evh_2095089031469157_clickPreventStop_38pvm5k9j,
    inst_evh_2095089031469157_clickPreventStop,
  };

  const evh_7315092382398562_reload_n7oxalmzg = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_n7oxalmzg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_rx76l8bcx = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_rx76l8bcx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_38pvm5k9j = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_38pvm5k9j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Project', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup flush>
        <ItpLabeledListGroupItem
          :label="pgettext('Project', 'Code')"
          :content="project.code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Project', 'Name')"
          :content="project.name"
        >
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
